import { useState } from 'react';
import type { AcpSelectConfigOption } from '@/components/desktop-config-controls';
import { useAiAgentSelection } from '@/hooks/use-conversation-settings';

/** The value a select option shows (`Opus 5.5`, `Medium`), groups looked into. */
export function selectedConfigName(option: AcpSelectConfigOption | undefined): string | null {
  if (option === undefined) return null;
  const choices = option.options.flatMap((choice) =>
    'options' in choice && Array.isArray(choice.options) ? choice.options : [choice],
  ) as readonly { value: string; name: string }[];
  return choices.find((choice) => choice.value === option.currentValue)?.name ?? null;
}

/**
 * Switches a conversation's agent as every agent picker does: the agent first, then its
 * saved profile cleared (it was the old agent's); a failure is said where the picker is.
 */
export function useComposerAgentSwitch({
  conversationId,
  agentId,
  selectAgent,
}: {
  conversationId: string;
  agentId: string;
  selectAgent: (agentId: string) => Promise<void>;
}): { switchAgent: (agentId: string) => void; error: string | null; clearError: () => void } {
  const aiAgent = useAiAgentSelection(conversationId);
  const [error, setError] = useState<string | null>(null);
  return {
    error,
    clearError: () => setError(null),
    switchAgent: (id) => {
      if (id === agentId) return;
      setError(null);
      void selectAgent(id)
        .then(() => aiAgent.select(null))
        .catch((reason: unknown) =>
          setError(reason instanceof Error ? reason.message : String(reason)),
        );
    },
  };
}
